import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { getUrlFriendlyPersonaId, getUrlFriendlyBlogId, PERSONA_DISPLAY_NAMES } from '../../utils/personaUtils';
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  Avatar,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { motion } from 'framer-motion';
import useAxiosAuth from '../../hooks/useAxiosAuth';

const AllBlogsPage = ({ user, onInteraction }) => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const personaNameMap = PERSONA_DISPLAY_NAMES;
  
  // Use your existing axios auth hook
  const axios = useAxiosAuth();

  // Persona name mapping
  // const personaNameMap = {
  //   "mecenna": "Restoration Artisan",
  //   "DIYMind": "DIYMind",
  //   "houseimprovemnts": "Practical Craftsman",
  //   "wilkerdo": "Fearless Builder",
  //   "woodshopdiaries": "Everyday Woodsmith",
  //   "fixthisbuildthat": "Builder Marksman",
  //   "everydayhomerepair": "House Doctor"
  // };
  
  // Colors for persona chips
  const personaColors = {
    "mecenna": "#8e44ad", // purple
    "DIYMind": "#3498db", // blue
    "houseimprovemnts": "#2ecc71", // green
    "wilkerdo": "#e74c3c", // red
    "woodshopdiaries": "#f39c12", // orange
    "fixthisbuildthat": "#1abc9c", // teal
    "everydayhomerepair": "#34495e" // dark blue
  };


useEffect(() => {
  const fetchAllBlogs = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get all personas first
      const personasResponse = await axios.get('/personas');
      const personas = personasResponse.data.personas || [];
      
      // Fetch blogs for each persona
      const allBlogs = [];
      
      for (const persona of personas) {
        try {
          // Convert internal persona ID to URL-friendly slug for API call
          const urlFriendlyPersonaId = getUrlFriendlyPersonaId(persona.id);
          const blogsResponse = await axios.get(`/blogs/${urlFriendlyPersonaId}`);
          
          if (blogsResponse.data.blogs && blogsResponse.data.blogs.length > 0) {
            // Add persona info to each blog
            const blogsWithPersona = blogsResponse.data.blogs.map(blog => ({
              ...blog,
              persona_id: persona.id,
              persona_name: personaNameMap[persona.id] || persona.id
            }));
            
            allBlogs.push(...blogsWithPersona);
          }
        } catch (err) {
          console.error(`Error fetching blogs for ${persona.id}:`, err);
          // Continue with other personas even if one fails
        }
      }
      
      // Only set blogs if we found any
      if (allBlogs.length > 0) {
        const shuffledBlogs = [...allBlogs].sort(() => Math.random() - 0.5);
        setBlogs(shuffledBlogs);
        setFilteredBlogs(shuffledBlogs);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching personas:', err);
      setError('Failed to load articles. Please try again later.');
      setLoading(false);
    }
  };
  
  fetchAllBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user, axios]);

  
  // Handle search input change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredBlogs(blogs);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = blogs.filter(blog => 
      blog.title?.toLowerCase().includes(term) || 
      blog.blog?.toLowerCase().includes(term) ||
      blog.persona_name?.toLowerCase().includes(term)
    );
    
    setFilteredBlogs(filtered);
  }, [searchTerm, blogs]);

const handleBlogClick = (blogId) => {
  if (!user) {
    onInteraction();
    return;
  }
  // Transform to URL-friendly format
  const urlFriendlyBlogId = getUrlFriendlyBlogId(blogId);
  navigate(`/blog/${urlFriendlyBlogId}`);
};
  
  
const handlePersonaClick = (personaId) => {
  if (!user) {
    onInteraction();
    return;
  }
  // Transform to URL-friendly format
  const urlFriendlyPersonaId = getUrlFriendlyPersonaId(personaId);
  navigate(`/persona/${urlFriendlyPersonaId}/blogs`);
};

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 6,
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/personal-projects')}
            sx={{ mt: 2 }}
          >
            Back to Projects
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Container maxWidth="lg">
                  {/* Back to Home Link - ADD THIS CODE */}
<Box sx={{ display: 'flex', mb: 4 }}>
  <Button
    startIcon={<ArrowBackIosIcon />}
    variant="text"
    onClick={() => navigate('/')}
    sx={{
      color: '#1e3c72',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      }
    }}
  >
    Back to DIYMind Home
  </Button>
</Box>
{/* End of Back to Home Link */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography 
            variant="h1" 
            gutterBottom
            sx={{
              background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              fontWeight: 700
            }}
          >
            All DIY Articles
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4 }}>
            Explore our complete collection of DIY articles from all our expert personas.
          </Typography>
          
          {/* Search field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search articles by title, content, or expert..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 4 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Divider sx={{ mb: 4 }} />
        </Box>
        
        {filteredBlogs.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h6" color="text.secondary">
              {searchTerm ? 'No articles match your search criteria.' : 'No articles available yet.'}
            </Typography>
            {searchTerm && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSearchTerm('')}
                sx={{ mt: 3 }}
              >
                Clear Search
              </Button>
            )}
          </Paper>
        ) : (
<Grid container spacing={3}>
  {filteredBlogs.map((blog, index) => (
    <Grid item xs={12} md={6} lg={4} key={blog.id} sx={{ display: 'flex' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.1 }}
        style={{ width: '100%' }}
      >
        <Card 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            cursor: 'pointer',
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: 6,
            },
          }}
          onClick={() => handleBlogClick(blog.id)}
        >
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Persona chip */}
            <Chip
              avatar={
                <Avatar 
                  sx={{ 
                    bgcolor: personaColors[blog.persona_id] || 'primary.main',
                  }}
                >
                  {blog.persona_id?.charAt(0)?.toUpperCase() || 'A'}
                </Avatar>
              }
              label={blog.persona_name || blog.persona_id}
              onClick={(e) => {
                e.stopPropagation();
                handlePersonaClick(blog.persona_id);
              }}
              sx={{ 
                mb: 2,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
            />
            
            {/* Title with fixed height */}
            <Typography 
              gutterBottom 
              variant="h5" 
              component="div"
              sx={{ 
                minHeight: '64px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {blog.title}
            </Typography>
            
            {/* Text content with fixed height */}
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                flexGrow: 1,
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                mb: 2
              }}
            >
              {blog.blog ? blog.blog.substring(0, 150) + (blog.blog.length > 150 ? '...' : '') : 'Click to read this article'}
            </Typography>
          </CardContent>
          
          <CardActions sx={{ p: 2, mt: 'auto' }}>
            <Button 
              size="small" 
              variant="contained"
              sx={{
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                },
                transition: 'all 0.3s'
              }}
            >
              Read Article
            </Button>
          </CardActions>
        </Card>
      </motion.div>
    </Grid>
  ))}
</Grid>
        )}
      </Container>
    </Box>
  );
};

export default AllBlogsPage;